import React, { useEffect, useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { NavLink, useLocation } from "react-router-dom";
import { Carousel, StagingBadge, Tab } from "@cinesys/ui/components";
import { useInterfaceAccess } from "../../hooks/useInterfaceAccess";
import {
  CONTROL_ROOM_TAB_KEY,
  SPOTTER_INTERFACE_ID,
  SPOTTER_TOP_TABS,
  SPOTTER_WORDMARK,
} from "./config";
import { SPOTTER_STRINGS } from "./strings";
import SpotterMark from "./SpotterMark";
import CinesysLogo, { CINESYS_URL } from "../../components/CinesysLogo";
import SpotterHeaderChanges from "./SpotterHeaderChanges";
import { SpotterChangesPanelProps } from "./SpotterChangesPanel";
import { useControlRoomNeedsAttention } from "./controlRoom/service";

/**
 * Spotter's own fully self-contained header — independent of `ShellHeader`, which only covers
 * customer-apps' own baseline surfaces, not Spotter/AB Vault/Metadata Import (see
 * `docs/decisions.md`). Own mark + wordmark + `StagingBadge`, org breadcrumb, its own tab
 * carousel, and a "Powered by Cinesys" badge, shared by `FieldsScreen`, `FrScreen`,
 * `GamebookScreen`, `RenameScreen`, `UsageScreen`, `ControlRoomScreen`.
 *
 * `CinesysLogo` is a separate "Powered by" mark at the far right — Cinesys is the maker, not the
 * product, so it stays secondary to the Spotter lockup. Tab identity (browser title) is set here,
 * not `public/index.html` (shared by every customer interface, so a global title would
 * brand-leak) — restored on unmount.
 *
 * Takes an optional `changes` prop (`SpotterChangesPanelProps`): when passed, `SpotterHeaderChanges`
 * renders a right-aligned "N unsaved changes" trigger in the tab row, a shortcut to the page's own
 * `SpotterChangesPanel` sidebar (see that component's doc comment for the mobile-vs-desktop
 * split).
 */
const MOBILE_MAX_WIDTH = "700px";

/** Sticky only above the mobile breakpoint (Addison) — on a phone-width screen a permanently-
 *  docked header eats too much of the already-short viewport, so it reverts to normal flow
 *  (scrolls away with the page) instead. `top: 0` — this is once again the topmost chrome on
 *  every Spotter route, not sitting below `ShellHeader`. */
const Header = styled.header`
  background: var(--spotterSlate);
  color: #e8ecef;
  padding: 13px 22px 0;
  position: sticky;
  top: 0;
  z-index: 30;

  --orangeChipBackground: var(--spotterAccentSoft);
  --orange400: var(--spotterAccent);
  --orangeBorder: var(--spotterAccentLine);

  @media (max-width: ${MOBILE_MAX_WIDTH}) {
    position: static;
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: nowrap;
`;

const Lockup = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  flex-shrink: 0;
`;

const Brand = styled.div`
  font-weight: 800;
  font-size: 22px;
  letter-spacing: -0.02em;
  color: #fff;
`;

/** \`min-width: 0\` + \`overflow: hidden\`/ellipsis so a long org name truncates instead of forcing
 *  \`Top\` to wrap or overflow. */
const Crumb = styled.div`
  font-size: 15px;
  color: var(--spotterHeaderMuted);
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  span {
    color: var(--spotterHeaderCrumbStrong);
    font-weight: 600;
  }
`;

/** Placeholder-width skeleton for the org name until `useInterfaceAccess` resolves to "granted" —
 *  never a fake name, never a bare separator dot. `MarkSkeleton`/`BrandSkeleton`/`TabsSkeleton`
 *  below do the same for the rest of the header, all keyed off the same `ready` boolean so an
 *  unauthenticated visitor can't tell what this tool is from the header alone. */
const CrumbSkeleton = styled.span`
  display: inline-block;
  width: 90px;
  height: 0.85em;
  border-radius: 3px;
  background: var(--spotterHeaderBarTrack);
  vertical-align: -0.1em;
`;

const MarkSkeleton = styled.span`
  display: inline-block;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--spotterHeaderBarTrack);
`;

const BrandSkeleton = styled.span`
  display: inline-block;
  width: 64px;
  height: 20px;
  border-radius: 4px;
  background: var(--spotterHeaderBarTrack);
`;

const TabPillSkeleton = styled.span`
  display: inline-block;
  height: 30px;
  border-radius: 6px;
  background: var(--spotterHeaderBarTrack);
  flex-shrink: 0;
`;

const TabsSkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

/** Same tab COUNT as `SPOTTER_TOP_TABS` (so the skeleton reads as "a tab row", not a random
 *  shape), but fixed widths, not derived from the real labels — deliberately uncorrelated with
 *  actual label length so it gives away nothing about what the tabs say. */
const SKELETON_TAB_WIDTHS = [70, 130, 90, 100, 70];

const SpotterTabsSkeleton = () => (
  <TabsSkeletonRow aria-hidden="true">
    {SPOTTER_TOP_TABS.map((tab, i) => (
      <TabPillSkeleton
        key={tab.key}
        style={{
          width: SKELETON_TAB_WIDTHS[i % SKELETON_TAB_WIDTHS.length],
        }}
      />
    ))}
  </TabsSkeletonRow>
);

const Spacer = styled.div`
  flex: 1;
`;

/** "Powered by Cinesys" — a real attribution link, kept secondary to the Spotter lockup.
 *  `flex-shrink: 0` so it never yields space to its siblings on desktop; dropped entirely below
 *  the mobile breakpoint so the org breadcrumb gets that room instead. */
const PoweredBy = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  text-decoration: none;
  opacity: 0.85;
  transition: opacity 0.15s ease;

  &:hover,
  &:focus-visible {
    opacity: 1;
  }

  @media (max-width: ${MOBILE_MAX_WIDTH}) {
    display: none;
  }
`;

const PoweredByLabel = styled.span`
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--spotterHeaderMuted);
  white-space: nowrap;
`;

/**
 * The tab row is the carousel variant at every viewport width now (Addison: an explicit reversal
 * of the earlier tablet/desktop `Tabs` 2-row-wrap + phone-only-carousel split — see below). It
 * reads `--gray300`/`--blue300` (inactive/active tab color) from the ambient `ColorProvider`
 * palette, overridden LOCALLY on `TabsRow` (their shared ancestor), same pattern
 * `CandidateAssignmentsScreen`'s `StyledSelect` already uses for `@cinesys/ui`'s `Select`.
 */
/** Individual tab button — reuses `Tabs`'s own exported `Tab` (identical active-state/hover
 *  styling, including react-router's automatic `.active` class via `NavLink`, see `Tab`'s own doc
 *  comment) rather than forking a second copy of that CSS. `flex-shrink: 0` so a tab's label
 *  never gets squeezed inside the carousel's `overflow-x: auto` row (same reasoning
 *  `TokenChip.tsx`'s own `TokenButton` uses `flex-shrink: 0` for, on its own carousel).
 *  `scroll-margin-inline` only affects where `scrollIntoView` decides a tab is "fully in view" —
 *  it doesn't affect layout, so the flush-edge resting state stays flush. Without it, a tab
 *  scrolled into view lands exactly flush against the container edge, under `Carousel`'s
 *  arrow/fade overlay (45px `FadeRight` plus the arrow button on top of it); 60px matches the
 *  margin Cantemo's own `Tabs.tsx` `scrollTabIntoView` uses for the same reason. */
const TabButton = styled(Tab)`
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 14px;
  border-bottom-width: 2.5px;
  white-space: nowrap;
  scroll-margin-inline: 60px;
`;

/** The Control Room tab's "something needs you" indicator (Addison: "show a little needs
 *  attention icon on the control room tab when you have stuff that needs you? Even when you're on
 *  a different tab?") — a plain inline dot next to the label rather than an absolutely-positioned
 *  corner badge, so it needs no extra positioning context on `TabButton`. Hidden while Control
 *  Room is already the active tab (`SpotterTabs` below) — no point flagging "something here needs
 *  you" on the page you're already looking at. */
const NeedsAttentionDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-left: 6px;
  border-radius: 50%;
  background: var(--spotterDanger);
  vertical-align: middle;
`;

/** The one tab bar, at every width — engages `Carousel`'s horizontal-scroll/fade/arrow behavior
 *  (arrows overlay the track, visible only when there's actually more to scroll to — see
 *  `Carousel.tsx`'s own doc comment) instead of wrapping tabs onto a second row, composing the
 *  already-ported `Carousel.tsx` (also what `TokenCarousel` in Mapping/Rename is built on) rather
 *  than duplicating Cantemo's own separate inline scroll/fade/arrow implementation. `flex: 1` +
 *  `min-width: 0` let it share `TabsRow` with `HeaderRightGroup` without pushing that group off
 *  the row — the carousel's own internal `overflow-x: auto` absorbs any extra tab width instead
 *  of the flex row doing it.
 *
 *  This replaces an earlier hybrid (a `Tabs`-based 2-row-wrap above 700px, this carousel only
 *  below it) that was built specifically to preserve the tablet 2-row-wrap UX — Addison's
 *  explicit follow-up ask was to drop that split and make the carousel the only tab bar at every
 *  width, tablet/desktop included. */
const TabsCarousel = styled(Carousel)`
  flex: 1;
  min-width: 0;
`;

/** Below the mobile breakpoint, `HeaderRightGroup`'s fixed-width contents (the progress meter's
 *  120px bar, see `FieldsScreen`'s `HeaderProgressBar`) don't leave the tab carousel enough
 *  width to stay usable alongside it, so mobile stacks the two into their own full-width rows
 *  instead of sharing one. Also the shared ancestor for `--gray300`/`--blue300` — see
 *  `TabsCarousel`'s doc comment above. */
const TabsRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
  flex-wrap: nowrap;
  --gray300: var(--spotterHeaderMuted);
  --blue300: var(--spotterAccentBright);

  @media (max-width: ${MOBILE_MAX_WIDTH}) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const HeaderRightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: auto;
  /* Unconditional (not just below the mobile breakpoint): without it the default flex-shrink: 1
     lets this group's fixed-width contents (e.g. the 120px progress bar in FieldsScreen's
     headerExtra) get squeezed narrower than their own box at any width TabsRow is tight on
     room. */
  flex-shrink: 0;

  @media (max-width: ${MOBILE_MAX_WIDTH}) {
    margin-left: 0;
    /* A page passing BOTH \`headerExtra\` and \`changes\` (only \`FieldsScreen\` does today) has two
     * fixed-width, non-shrinking children here — side by side they're wider than a phone
     * viewport, pushing the whole page into horizontal scroll. Wrapping keeps each on its own
     * line instead. */
    flex-wrap: wrap;
  }
`;

/** See "Tab identity" in the doc comment above for why this lives here and not in
 *  `public/index.html`. Takes `ready` (`useInterfaceAccess`'s "granted" check) — the BROWSER tab
 *  strip is as much "what the tool's about" as anything rendered in-page, so it stays on whatever
 *  title/favicon the document already had until access is actually granted, same as every other
 *  identifying bit of this header. */
const useSpotterTabIdentity = (ready: boolean) => {
  useEffect(() => {
    if (!ready) {
      return undefined;
    }
    const previousTitle = document.title;
    document.title = SPOTTER_WORDMARK;

    return () => {
      document.title = previousTitle;
    };
  }, [ready]);
};

/** Publishes this header's REAL rendered height as `--spotterHeaderHeight` on the document root, so
 *  `FieldsScreen`/`FrScreen`/`RenameScreen`'s sticky `Aside`/`PageWrapperTwoColumn` `stickyTop` can sit
 *  flush under the header (`var(--spotterHeaderHeight, 118px)`) instead of a hand-tuned pixel guess —
 *  the header's actual height drifts as its own content changes (org-name breadcrumb loading in,
 *  a font swap, tab label wrapping on a narrow window), and a stale hardcoded number either left
 *  a gap or, worse, let the sticky pane render partially UNDER the header (same `z-index` stack
 *  but visually starting too high). `ResizeObserver` keeps it correct across those changes, not
 *  just at mount. Global (`documentElement`), since `Aside` is a DOM sibling of `Header`, not a
 *  descendant — a CSS var only reaches it via a shared ancestor. Reset on unmount for the same
 *  route-scoping reason `useSpotterTabIdentity` restores the title. */
const useHeaderHeightVar = (ref: React.RefObject<HTMLElement>) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const setVar = () => {
      document.documentElement.style.setProperty(
        "--spotterHeaderHeight",
        `${el.getBoundingClientRect().height}px`
      );
    };
    setVar();
    const observer = new ResizeObserver(setVar);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--spotterHeaderHeight");
    };
  }, [ref]);
};

/** Each `spotter/*` tab is its own route (`AppRouter.tsx`), and every one of those screens
 *  renders its own `SpotterHeader` — so `SpotterTabs` fully unmounts and remounts on every tab
 *  click, losing the carousel's scroll position with it. Left alone, a fresh mount always starts
 *  at `scrollLeft: 0`, so the scroll-into-view effect below would visibly slide the WHOLE way
 *  over from the left edge on every navigation, even if the user had already been scrolled over
 *  toward the target tab. A plain module-scoped variable (not component state, which resets on
 *  remount same as the DOM does) survives that remount so the next mount can restore it. */
let lastTabsScrollLeft = 0;

/** `TabsCarousel`'s tab row — separate component (not inlined in `SpotterHeader`) purely so the
 *  active-tab-scroll-into-view effect below has its own clean dependency array keyed on the
 *  route, without dragging `SpotterHeader`'s own unrelated state into that effect's deps.
 *  Auto-scrolls the active tab into view (mirrors Cantemo's own `Tabs`'s `scrollTabIntoView`) via
 *  the DOM's native `scrollIntoView` on the active tab's own element rather than reimplementing
 *  Cantemo's manual scrollLeft/margin math — the tab sits inside `Carousel`'s own
 *  `overflow-x: auto` container, which is exactly what `scrollIntoView({ inline: "nearest" })`
 *  already knows how to scroll. `block: "nearest"` keeps this from ever scrolling the PAGE
 *  vertically — only relevant if the header were off-screen when a tab is activated, which can't
 *  happen here since tapping a tab requires the header (and thus the tab itself) to already be
 *  visible. `scroll-margin-inline` on `TabButton` keeps the scrolled-to tab clear of the arrow/
 *  fade overlay (see its own doc comment) instead of landing flush under it. */
const SpotterTabs = ({ needsAttention }: { needsAttention: boolean }) => {
  const location = useLocation();
  const tabRefs = useRef<Record<string, HTMLElement | null>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeKey = SPOTTER_TOP_TABS.find((t) =>
    location.pathname.startsWith(t.key)
  )?.key;

  // Runs before paint, so the restore never flashes at scrollLeft 0 first — see
  // `lastTabsScrollLeft`'s doc comment above for why this is needed at all.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollLeft = lastTabsScrollLeft;
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const trackScrollLeft = () => {
      lastTabsScrollLeft = el.scrollLeft;
    };
    el.addEventListener("scroll", trackScrollLeft);
    return () => el.removeEventListener("scroll", trackScrollLeft);
  }, []);

  useEffect(() => {
    if (!activeKey) {
      return;
    }
    tabRefs.current[activeKey]?.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    });
  }, [activeKey]);

  return (
    <TabsCarousel
      arrowSize={28}
      arrowIconSize={16}
      justify="start"
      fadeColor="var(--spotterSlate)"
      scrollRef={scrollRef}
    >
      {SPOTTER_TOP_TABS.map((tab) => (
        <TabButton
          key={tab.key}
          as={NavLink}
          to={tab.key}
          ref={(element: HTMLElement | null) => {
            tabRefs.current[tab.key] = element;
          }}
        >
          {tab.label}
          {tab.key === CONTROL_ROOM_TAB_KEY &&
            needsAttention &&
            activeKey !== CONTROL_ROOM_TAB_KEY && (
              <NeedsAttentionDot
                role="img"
                aria-label={SPOTTER_STRINGS.controlRoom.needsAttentionTabDot}
              />
            )}
        </TabButton>
      ))}
    </TabsCarousel>
  );
};

interface SpotterHeaderProps {
  changes?: SpotterChangesPanelProps;
  /** Optional extra content rendered in the tab row, grouped with and left of the changes trigger. */
  headerExtra?: React.ReactNode;
}

/** Only renders header CHROME — the "denied" content notice is each mount site's own job (every
 *  screen already gates its content area on `useInterfaceAccess` before rendering this, see
 *  `FieldsScreen.tsx` etc.). Rendering a notice HERE too would double it up under every screen
 *  that already shows its own. */
const SpotterHeader = ({ changes, headerExtra }: SpotterHeaderProps) => {
  const access = useInterfaceAccess(SPOTTER_INTERFACE_ID);
  const ready = access.status === "granted";
  const orgName = access.status === "granted" ? access.customerName : undefined;
  const headerRef = useRef<HTMLElement>(null);
  useSpotterTabIdentity(ready);
  useHeaderHeightVar(headerRef);
  const needsAttention = useControlRoomNeedsAttention(ready);

  return (
    <Header ref={headerRef}>
      <Top>
        <Lockup>
          {ready ? (
            <>
              <SpotterMark />
              <Brand>{SPOTTER_WORDMARK}</Brand>
            </>
          ) : (
            <>
              <MarkSkeleton aria-hidden="true" />
              <BrandSkeleton aria-hidden="true" />
            </>
          )}
          <StagingBadge />
        </Lockup>
        <Crumb>
          {SPOTTER_STRINGS.header.breadcrumbProduct}{" "}
          {ready ? (
            <>
              &nbsp;·&nbsp; <span>{orgName}</span>
            </>
          ) : (
            <>
              &nbsp;·&nbsp; <CrumbSkeleton aria-hidden="true" />
            </>
          )}
        </Crumb>
        <Spacer />
        <PoweredBy href={CINESYS_URL} target="_blank" rel="noopener noreferrer">
          <PoweredByLabel>Powered by</PoweredByLabel>
          <CinesysLogo height={28} />
        </PoweredBy>
      </Top>
      <TabsRow>
        {ready ? (
          <SpotterTabs needsAttention={needsAttention} />
        ) : (
          <SpotterTabsSkeleton />
        )}
        {ready && (headerExtra || changes) && (
          <HeaderRightGroup>
            {headerExtra}
            {changes && <SpotterHeaderChanges {...changes} />}
          </HeaderRightGroup>
        )}
      </TabsRow>
    </Header>
  );
};

export default SpotterHeader;
